import { Box, CircularProgress, Typography } from '@deere/fuel-react';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertBox } from '../../components/AlertBox.jsx';
import { BidForm } from '../../components/BidForm.jsx';
import { AdminContext } from '../../context/AdminContext';
import styles from './Page.module.css';
import { LoggedInContext } from '../../context/LoggedInContext.jsx';
import { errorBannerFromResponse } from '../../utils/errorBanner';

export const CreateBid = () => {
    const { selectedAdminGroup, error: contextError } =
        useContext(AdminContext);
    const [submitError, setSubmitError] = useState(false);
    const [alertMsg, setAlertMsg] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [classificationList, setClassificationList] = useState([]);
    const navigate = useNavigate();
    const location = useLocation();
    const { makeRequest, authState } = useContext(LoggedInContext);

    const createBid = async (bid, isValid) => {
        const requestOptions = {
            method: 'POST',
            body: JSON.stringify(bid),
        };
        if (!isValid) {
            setAlertMsg(
                'You must fill out all required fields to create a new job bid.'
            );
            setSubmitError(true);
            window.scrollTo(0, 0);
        } else {
            setSubmitError(false);
            const response = await makeRequest(
                process.env.REACT_APP_API_ENDPOINT + '/jobs',
                requestOptions
            );
            if (response.ok) {
                const text = await response.text();
                navigate('/pending-jobs', {
                    state: {
                        variant: 'success',
                        message:
                            'You have successfully created a new Pending Job Bid! The job number assigned to this new bid is ' +
                            text,
                    },
                });
            } else {
                window.scrollTo(0, 0);
                setSubmitError(true);
                setAlertMsg(
                    errorBannerFromResponse(
                        response,
                        'Your job bid was not saved. Please try again later.'
                    ).message
                );
            }
        }
    };

    useEffect(() => {
        let ignore = false;
        const fetchClassification = async () => {
            setIsLoading(true);
            try {
                const response = await makeRequest(
                    `${process.env.REACT_APP_API_ENDPOINT}/classification/${selectedAdminGroup.adGroup}`
                );
                if (!ignore) {
                    if (response.status === 200) {
                        const responseData = await response.json();
                        const sortedData = [...responseData].sort((a, b) =>
                            a.classification.localeCompare(b.classification)
                        );
                        setClassificationList(sortedData);
                        setIsLoading(false);
                    } else {
                        setAlertMsg(
                            errorBannerFromResponse(
                                response,
                                'We are experiencing technical issues. Please try again later.'
                            ).message
                        );
                        setIsLoading(false);
                    }
                }
            } catch (err) {
                setAlertMsg(
                    errorBannerFromResponse(
                        err,
                        'We are experiencing technical issues. Please try again later.'
                    ).message
                );
            }
        };
        fetchClassification();
        // cleanup function that will ignore future results of fetch triggered by the useEffect hook
        return () => {
            ignore = true;
        };
    }, [authState, makeRequest, selectedAdminGroup]);

    return (
        <div className={styles.parentContainer}>
            <Box spacing={6} className={styles.contentToContainer}>
                <Box
                    className={styles.headerContainer}
                    display="block"
                    bgcolor="background.default"
                >
                    {(submitError || alertMsg !== null) && (
                        <AlertBox msg={alertMsg} variant={'error'} />
                    )}
                    {contextError && (
                        <AlertBox
                            msg={
                                errorBannerFromResponse(
                                    contextError,
                                    'We are experiencing technical issues. Please try again later.'
                                ).message
                            }
                            variant={'error'}
                        />
                    )}
                    <Typography variant="h1" className={styles.heading}>
                        Create a New Job
                    </Typography>
                    Fill out the form below and click submit to create a new job
                    posting. You will be able to edit this job from the pending
                    jobs list up until the day it posts.
                </Box>
                <Box className={styles.required}>
                    <span>* </span>
                    Required fields.
                </Box>
                {isLoading && (
                    <CircularProgress
                        size="lg"
                        variant="indeterminate"
                        title={'Loading'}
                    />
                )}
                {!isLoading && (
                    <BidForm
                        bidObj={location.state?.bidObj ?? {}}
                        activeCopyCallCr={
                            location.state?.activeJobCopyCall ?? false
                        }
                        updateBid={createBid}
                        classificationList={classificationList}
                    />
                )}
            </Box>
        </div>
    );
};
